import React, { Suspense, lazy, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Modal } from "components/hds";
import { Spinner } from "components/ui";
import { useScenes } from "contexts/Scenes";
import { useAuth } from "contexts/Auth";
import { useSceneDocStore } from "contexts/Scenes/useSceneDocStore";
import { StreamingState } from "views/SharedScene/StreamingStatesConstants";
import {
	requestViewerFocus,
	VIEWER_FOCUS_CONFIGURATOR,
	VIEWER_FOCUS_NAVIGATOR,
} from "views/Projects/views/Scene/viewerFocus";
import { useLiveViewStore } from "./useLiveViewStore";

// Lazy, not a static import: this file is reached from the `components`
// barrel (index → Header → LiveViewButton → here), and PStreaming imports
// `Containers` back from that same barrel — statically, it would evaluate
// while the barrel is still half-initialised and find Containers undefined.
// Fetched when a start begins (see the starting effect), so the chunk is there
// by the time the dialog opens.
const loadPStreaming = () => import("views/Projects/views/Scene/tabs/PStreaming");
const PStreaming = lazy(loadPStreaming);

/** How long a closed dialog keeps its streaming session for a quick reopen. */
const HIDDEN_SESSION_TTL_MS = 5 * 60 * 1000;

/**
 * Longest the button spins before the dialog opens anyway: a render-server
 * queue can take minutes, and the dialog is where its status line is shown.
 */
const START_OPEN_TIMEOUT_MS = 45 * 1000;

/** States in which the Navigator is loading or showing the scene — the dialog opens here. */
const SCENE_STATES: string[] = [StreamingState.LOADING_SCENE, StreamingState.SCENE_LOADED];

/**
 * The Navigator in a dialog almost as large as the window — the b2c editor's
 * Live View, where the regular editor has a flexlayout tab instead.
 *
 * The body is the Navigator tab itself (PStreaming): every bit of its state
 * lives in ScenesProvider, which wraps the header too, so it works the same
 * outside flexlayout — email-verification gate, queue status, AFK overlay and
 * tutorial included.
 *
 * Opening runs in two steps. Once the scene is on the server (the store's
 * `starting`), the session is started — or a running one reused — while the
 * dialog stays shut and the header button spins, so the user keeps working in
 * the Configurator through the slow part. The dialog opens when the Navigator
 * starts loading the scene. Minimizing it keeps the session for a while, closing ends it — see
 * handleMinimize and handleClose.
 */
const LiveViewModal = () => {
	const { t } = useTranslation();
	const status = useLiveViewStore((state) => state.status);
	const open = status === "open";
	const close = useLiveViewStore((state) => state.close);
	const minimize = useLiveViewStore((state) => state.minimize);
	const markOpen = useLiveViewStore((state) => state.markOpen);
	const {
		arcwareApplication,
		streamingState,
		setStreamingState,
		streamingCloseEvent,
		setStreamingCloseEvent,
		closeStreaming,
		reloadStreamingScene,
		isStreamingCleaningUp,
	} = useScenes();
	// Same reading as PStreaming's gate: absent on accounts that predate it.
	const { me } = useAuth();
	const emailVerified = me?.profile?.emailVerified !== false;

	// The keyboard belongs to the Navigator while the dialog is up: this turns
	// the editor's shortcuts off (and back on after), the same signal a click
	// into the Navigator tab sends.
	useEffect(() => {
		if (!open) return undefined;
		requestViewerFocus(VIEWER_FOCUS_NAVIGATOR);
		return () => requestViewerFocus(VIEWER_FOCUS_CONFIGURATOR);
	}, [open]);

	// Read at timer/unmount time, when the render that scheduled them is stale:
	// closeStreaming only finds the SDK instances of the latest render.
	const endSessionRef = useRef<() => void>(() => {});
	endSessionRef.current = () => {
		setStreamingCloseEvent?.(null);
		closeStreaming?.();
		setStreamingState?.(StreamingState.WELCOME);
	};

	// Minimizing the dialog only hides it: the session stays up, so reopening it
	// shortly after shows the scene in seconds instead of waiting for a render
	// server again. The stream runs on a paid server, though, so a session left
	// hidden is ended after HIDDEN_SESSION_TTL_MS — or earlier by the streaming
	// side's own AFK timeout, in which case reopening starts a new one.
	const hiddenTimerRef = useRef<number | null>(null);
	const clearHiddenTimer = () => {
		if (hiddenTimerRef.current !== null) {
			window.clearTimeout(hiddenTimerRef.current);
			hiddenTimerRef.current = null;
		}
	};
	const scheduleHiddenEnd = (delay: number) => {
		clearHiddenTimer();
		hiddenTimerRef.current = window.setTimeout(() => {
			hiddenTimerRef.current = null;
			const current = useLiveViewStore.getState().status;
			if (current === "idle") endSessionRef.current();
			// A reopen is saving right now: decide once it has either gone on
			// to starting (which clears this) or failed (back to idle).
			else if (current === "saving") scheduleHiddenEnd(10000);
		}, delay);
	};
	useEffect(() => {
		if (status === "starting" || status === "open") clearHiddenTimer();
	}, [status]);

	// The editor going away (scene closed, leaving b2c) takes the button and
	// this dialog with it — and with them every way back to a hidden session.
	// Only a session this dialog started or showed: one started anywhere else
	// is not ours to end.
	const statusRef = useRef(status);
	statusRef.current = status;
	useEffect(
		() => () => {
			const ownsSession = statusRef.current === "open"
				|| statusRef.current === "starting"
				|| hiddenTimerRef.current !== null;
			clearHiddenTimer();
			if (ownsSession) endSessionRef.current();
		},
		[],
	);

	// The scene document's identity changes on every save (the store is
	// patched with what was persisted) and on nothing else, so comparing it
	// with the one the dialog was closed on tells a reopen whether the hidden
	// session's copy of the scene is outdated: a reopen with no save in between
	// shows the running session as it is, without a reload.
	const doc = useSceneDocStore((state) => state.doc);
	const docAtCloseRef = useRef<unknown>(null);

	// ── starting: get a session to the scene while the dialog stays shut ──
	//
	// `kicked` is set once this start has done its one action (start, reuse
	// or reload); after that the effect only waits for a state to open on.
	// Every way out ends in `open`, failures included: the dialog is where
	// the Navigator shows errors and its start screen, and a button that just
	// stopped spinning would explain nothing.
	const kickedRef = useRef(false);
	useEffect(() => {
		if (status !== "starting") {
			kickedRef.current = false;
			return undefined;
		}
		loadPStreaming();
		// The verification gate lives in the dialog; nothing to start.
		if (!emailVerified) {
			markOpen();
			return undefined;
		}

		if (!kickedRef.current) {
			// A session the streaming side ended while hidden (AFK, error):
			// nothing to reuse. Reset it; the next run starts a new one.
			if (streamingCloseEvent || streamingState === StreamingState.CLOSED_ON_AFK_ERROR) {
				setStreamingCloseEvent?.(null);
				closeStreaming?.();
				setStreamingState?.(StreamingState.WELCOME);
				return undefined;
			}
			if (streamingState === StreamingState.WELCOME) {
				// The start button waits out a cleanup still running, too.
				if (isStreamingCleaningUp) return undefined;
				kickedRef.current = true;
				setStreamingState?.(StreamingState.READY_TO_INIT);
				return undefined;
			}
			// A session is running or on its way — reuse it, the way a scene
			// switch in the Navigator tab does. If the scene was saved since it
			// last showed it, have it re-read the scene (forceupdate); one still
			// loading picks up the saved state anyway.
			kickedRef.current = true;
			if (
				streamingState === StreamingState.SCENE_LOADED
				&& docAtCloseRef.current !== doc
			) {
				reloadStreamingScene?.();
				return undefined;
			}
		}

		if (
			SCENE_STATES.includes(String(streamingState))
			|| streamingCloseEvent
			|| streamingState === StreamingState.CLOSED_ON_AFK_ERROR
			// Back on the start screen after the kick: the SDK failed to start.
			|| (streamingState === StreamingState.WELCOME && !isStreamingCleaningUp)
		) {
			markOpen();
		}
		return undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, emailVerified, streamingState, streamingCloseEvent, isStreamingCleaningUp]);

	useEffect(() => {
		if (status !== "starting") return undefined;
		const timer = window.setTimeout(markOpen, START_OPEN_TIMEOUT_MS);
		return () => window.clearTimeout(timer);
	}, [status, markOpen]);

	// The SDK's root element (video included) belongs in a document while the
	// session starts: kept out of it, the video has nowhere to play and nothing
	// to size itself by. Parked off-screen here until PStreaming, mounted by
	// the dialog, moves it into its own container.
	const parkingRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const rootElement = arcwareApplication?.rootElement;
		if (status !== "starting" || !rootElement || !parkingRef.current) return;
		if (!rootElement.isConnected) parkingRef.current.appendChild(rootElement);
	}, [status, arcwareApplication]);

	// Minimize (also Escape and a click beside the dialog — see hds Modal):
	// put the dialog away and keep the session for a quick reopen.
	const handleMinimize = () => {
		docAtCloseRef.current = useSceneDocStore.getState().doc;
		minimize();
		scheduleHiddenEnd(HIDDEN_SESSION_TTL_MS);
	};

	// Close: the user is done — end the paid session now, the way the
	// Navigator's own close button does.
	const handleClose = () => {
		close();
		clearHiddenTimer();
		endSessionRef.current();
	};

	return (
		<>
			<Parking ref={parkingRef} aria-hidden="true" />
			<Modal
				open={open}
				onClose={handleClose}
				onMinimize={handleMinimize}
				minimizeLabel={String(t("app.liveView.minimize"))}
				minimizeTooltip={String(t("app.liveView.minimizeHint"))}
				closeTooltip={String(t("app.liveView.closeHint"))}
				size="viewport"
				title={String(t("app.liveView.title"))}
			>
				<Suspense fallback={<Spinner />}>
					<PStreaming viewerInFocus={VIEWER_FOCUS_NAVIGATOR} showPoweredBy />
				</Suspense>
			</Modal>
		</>
	);
};

/* Viewport-sized, so the stream starts at the resolution it will be shown at,
   but outside it and inert. */
const Parking = styled.div`
	position: fixed;
	top: 0;
	left: -200vw;
	width: 100vw;
	height: 100vh;
	overflow: hidden;
	pointer-events: none;
`;

export default LiveViewModal;
